/* Season trips from Vijayawada (Oct 2026, from the signed-off mock Trips_2): the trips an
   admin can show on Home, each with its own page at /trips/<slug>. Which ones show, in
   what order, and the section's title are set in Admin > Pricing > Season trips (the
   SEASON_TRIPS config); the trips themselves live here.

   Prices are never written here. They are worked out on the page from the live cars and
   the city's card with the same calculateFare the car cards use, so a trip's "from ₹" is
   always a price a customer can actually be quoted - and an owner's own prices are only
   read, never changed.

   No imports: scripts/prerenderRoutes.mjs and the backend tests load this file as it is. */

export const SEASON_CITY = "Vijayawada";
export const SEASON_FROM = { value: "Vijayawada, Andhra Pradesh, India", selected: true };
export const MAX_SEASON_TRIPS = 8;
export const SEASON_TITLES = ["KARTHIKA", "SANKRANTI", "SUMMER", "POPULAR"];

const L = (te, en, hi, ta, kn) => ({ te, en, hi, ta, kn });

/* A drop from Vijayawada to a town (Oct 2026, mock Seo_1): its own page and a row on
   /trips, for the people who search "Vijayawada to <town> cab". Not on the Home banner
   unless an admin picks it. km and hours are one way, the road distance and the time. */
const HOURS_ABOUT = (h) => (h === 1
  ? L("సుమారు గంట", "about an hour", "लगभग एक घंटा", "சுமார் ஒரு மணி நேரம்", "ಸುಮಾರು ಒಂದು ಗಂಟೆ")
  : L(`సుమారు ${h} గంటలు`, `about ${h} hours`, `लगभग ${h} घंटे`, `சுமார் ${h} மணி நேரம்`, `ಸುಮಾರು ${h} ಗಂಟೆ`));
function dropTrip(slug, km, hours, to, name, abbr) {
  const a = HOURS_ABOUT(hours);
  return {
    slug, type: "ONE_WAY", km, hours, when: ["DROP"], drop: true, name, ...(abbr ? { abbr } : {}), to,
    note: L(`విజయవాడ నుండి ${name.te} వరకు దిగబెట్టడం - ${a.te}.`, `A drop from Vijayawada to ${name.en} - ${a.en}.`,
      `विजयवाड़ा से ${name.hi} तक छोड़ना - ${a.hi}।`, `விஜயவாடாவிலிருந்து ${name.ta} வரை - ${a.ta}.`,
      `ವಿಜಯವಾಡದಿಂದ ${name.kn} ವರೆಗೆ ಬಿಡುವುದು - ${a.kn}.`),
  };
}

/* km: the whole trip, there and back for a round trip. hours: the time the car is
   with the customer. when: how the card says it - [kind, n]. dest: where it goes, for
   "Vijayawada → ..." when it differs from the trip's name; abbr: a shorter English one
   for the Home banner. Home shows each trip's lowest price, whatever the car type. */
export const SEASON_TRIPS = [
  {
    slug: "pancharama", type: "ROUND", km: 520, hours: 18, when: ["DAY", 1], tag: "KARTHIKA",
    name: L("పంచారామాలు", "Pancharama temples", "पंचाराम मंदिर", "பஞ்சாராம கோயில்கள்", "ಪಂಚಾರಾಮ ದೇವಾಲಯಗಳು"),
    dest: L("పంచారామాలు", "Pancharama", "पंचाराम", "பஞ்சாராமம்", "ಪಂಚಾರಾಮ"),
    to: "Samarlakota, Andhra Pradesh, India",
    stops: [
      { v: "Amaravati, Andhra Pradesh, India", n: L("అమరావతి", "Amaravati", "अमरावती", "அமராவதி", "ಅಮರಾವತಿ") },
      { v: "Bhimavaram, Andhra Pradesh, India", n: L("భీమవరం", "Bhimavaram", "भीमावरम", "பீமவரம்", "ಭೀಮವರಂ") },
      { v: "Palakollu, Andhra Pradesh, India", n: L("పాలకొల్లు", "Palakollu", "पालकोल्लु", "பாலகொல்லு", "ಪಾಲಕೊಲ್ಲು") },
      { v: "Draksharamam, Andhra Pradesh, India", n: L("ద్రాక్షారామం", "Draksharamam", "द्राक्षारामम", "திராக்ஷாராமம்", "ದ್ರಾಕ್ಷಾರಾಮ") },
    ],
    end: L("సామర్లకోట", "Samarlakota", "सामर्लकोटा", "சாமர்லகோட்டா", "ಸಾಮರ್ಲಕೋಟ"),
    note: L("తెల్లవారుజామున 4 కి బయలుదేరితే ఐదు గుడులు చూసి రాత్రికి తిరిగి రావచ్చు.",
      "Leave at 4 am, see all five temples and be back the same night.",
      "सुबह 4 बजे निकलें, पाँचों मंदिर देखकर उसी रात लौट आएँ।",
      "அதிகாலை 4 மணிக்கு புறப்பட்டால் ஐந்து கோயில்களையும் பார்த்து அன்றிரவே திரும்பலாம்.",
      "ಬೆಳಿಗ್ಗೆ 4ಕ್ಕೆ ಹೊರಟರೆ ಐದೂ ದೇವಾಲಯ ನೋಡಿ ಅದೇ ರಾತ್ರಿ ಹಿಂತಿರುಗಬಹುದು."),
  },
  {
    slug: "srisailam", type: "ROUND", km: 530, hours: 30, when: ["DAYS", 2], tag: "KARTHIKA",
    name: L("శ్రీశైలం", "Srisailam", "श्रीशैलम", "ஸ்ரீசைலம்", "ಶ್ರೀಶೈಲ"),
    to: "Srisailam, Andhra Pradesh, India",
    note: L("ఒక రాత్రి అక్కడ ఉండి, మల్లికార్జున స్వామి దర్శనం చేసుకుని మరుసటి రోజు తిరిగి.",
      "Stay one night for Mallikarjuna darshan and come back the next day.",
      "एक रात रुककर मल्लिकार्जुन दर्शन करें और अगले दिन लौटें।",
      "ஒரு இரவு தங்கி மல்லிகார்ஜுனர் தரிசனம் செய்து மறுநாள் திரும்பலாம்.",
      "ಒಂದು ರಾತ್ರಿ ಉಳಿದು ಮಲ್ಲಿಕಾರ್ಜುನ ದರ್ಶನ ಮಾಡಿ ಮರುದಿನ ಹಿಂತಿರುಗಿ."),
  },
  {
    slug: "sabarimala", type: "ROUND", km: 2350, hours: 120, when: ["DAYS", 5], tag: "AYYAPPA", group: true,
    name: L("శబరిమల", "Sabarimala", "सबरीमाला", "சபரிமலை", "ಶಬರಿಮಲೆ"),
    to: "Sabarimala, Kerala, India",
    note: L("అయ్యప్ప మాల వేసిన బృందానికి - 7 సీటర్ లేదా టెంపో, 5 రోజుల యాత్ర.",
      "For an Ayyappa group - a 7-seater or a tempo traveller, a 5-day trip.",
      "अय्यप्पा समूह के लिए - 7 सीटर या टेम्पो, 5 दिन की यात्रा।",
      "ஐயப்ப பக்தர் குழுவுக்கு - 7 இருக்கை அல்லது டெம்போ, 5 நாள் பயணம்.",
      "ಅಯ್ಯಪ್ಪ ಮಾಲಾಧಾರಿ ಗುಂಪಿಗೆ - 7 ಸೀಟರ್ ಅಥವಾ ಟೆಂಪೋ, 5 ದಿನಗಳ ಯಾತ್ರೆ."),
  },
  {
    slug: "tirupati", type: "ROUND", km: 820, hours: 36, when: ["DAYS", 2],
    name: L("తిరుపతి", "Tirupati", "तिरुपति", "திருப்பதி", "ತಿರುಪತಿ"),
    to: "Tirumala, Andhra Pradesh, India",
    note: L("రాత్రి బయలుదేరి, దర్శనం చేసుకుని మరుసటి రోజు రాత్రికి తిరిగి.",
      "Leave at night, have darshan and be back the next night.",
      "रात को निकलें, दर्शन करें और अगली रात लौटें।",
      "இரவு புறப்பட்டு, தரிசனம் முடித்து மறுநாள் இரவு திரும்பலாம்.",
      "ರಾತ್ರಿ ಹೊರಟು, ದರ್ಶನ ಮಾಡಿ ಮರುದಿನ ರಾತ್ರಿ ಹಿಂತಿರುಗಿ."),
  },
  {
    slug: "hyderabad", type: "ROUND", km: 550, hours: 14, when: ["SAME"],
    name: L("హైదరాబాద్", "Hyderabad", "हैदराबाद", "ஹைதராபாத்", "ಹೈದರಾಬಾದ್"), abbr: "Hyd",
    to: "Hyderabad, Telangana, India",
    note: L("ఉదయం వెళ్ళి పని చూసుకుని అదే రోజు రాత్రికి తిరిగి.",
      "Go in the morning, finish your work and be back the same night.",
      "सुबह जाएँ, काम निपटाएँ और उसी रात लौटें।",
      "காலையில் சென்று வேலை முடித்து அன்றிரவே திரும்பலாம்.",
      "ಬೆಳಿಗ್ಗೆ ಹೋಗಿ ಕೆಲಸ ಮುಗಿಸಿ ಅದೇ ರಾತ್ರಿ ಹಿಂತಿರುಗಿ."),
  },
  {
    slug: "gannavaram-airport", type: "ONE_WAY", km: 20, hours: 1, when: ["DROP"],
    name: L("గన్నవరం విమానాశ్రయం", "Gannavaram airport", "गन्नवरम हवाई अड्डा", "கன்னவரம் விமான நிலையம்", "ಗನ್ನವರಂ ವಿಮಾನ ನಿಲ್ದಾಣ"), abbr: "Airport",
    to: "Vijayawada International Airport, Gannavaram, Andhra Pradesh, India",
    note: L("విజయవాడ నుండి విమానాశ్రయానికి దిగబెట్టడం - సుమారు 40 నిమిషాలు.",
      "A drop from Vijayawada to the airport - about 40 minutes.",
      "विजयवाड़ा से हवाई अड्डे तक छोड़ना - लगभग 40 मिनट।",
      "விஜயவாடாவிலிருந்து விமான நிலையம் வரை - சுமார் 40 நிமிடம்.",
      "ವಿಜಯವಾಡದಿಂದ ವಿಮಾನ ನಿಲ್ದಾಣಕ್ಕೆ ಬಿಡುವುದು - ಸುಮಾರು 40 ನಿಮಿಷ."),
  },
  {
    slug: "guntur", type: "ONE_WAY", km: 35, hours: 1, when: ["DROP"],
    name: L("గుంటూరు", "Guntur", "गुंटूर", "குண்டூர்", "ಗುಂಟೂರು"),
    to: "Guntur, Andhra Pradesh, India",
    note: L("విజయవాడ నుండి గుంటూరుకు దిగబెట్టడం - సుమారు గంట.",
      "A drop from Vijayawada to Guntur - about an hour.",
      "विजयवाड़ा से गुंटूर तक छोड़ना - लगभग एक घंटा।",
      "விஜயவாடாவிலிருந்து குண்டூர் வரை - சுமார் ஒரு மணி நேரம்.",
      "ವಿಜಯವಾಡದಿಂದ ಗುಂಟೂರಿಗೆ ಬಿಡುವುದು - ಸುಮಾರು ಒಂದು ಗಂಟೆ."),
  },
  {
    slug: "hyderabad-airport", type: "ONE_WAY", km: 290, hours: 5, when: ["DROP"],
    name: L("హైదరాబాద్ విమానాశ్రయం", "Hyderabad airport", "हैदराबाद हवाई अड्डा", "ஹைதராபாத் விமான நிலையம்", "ಹೈದರಾಬಾದ್ ವಿಮಾನ ನಿಲ್ದಾಣ"),
    to: "Rajiv Gandhi International Airport, Shamshabad, Telangana, India",
    note: L("విజయవాడ నుండి శంషాబాద్ విమానాశ్రయానికి దిగబెట్టడం - సుమారు 5 గంటలు.",
      "A drop from Vijayawada to Shamshabad airport - about 5 hours.",
      "विजयवाड़ा से शमशाबाद हवाई अड्डे तक छोड़ना - लगभग 5 घंटे।",
      "விஜயவாடாவிலிருந்து ஷம்ஷாபாத் விமான நிலையம் வரை - சுமார் 5 மணி நேரம்.",
      "ವಿಜಯವಾಡದಿಂದ ಶಂಶಾಬಾದ್ ವಿಮಾನ ನಿಲ್ದಾಣಕ್ಕೆ ಬಿಡುವುದು - ಸುಮಾರು 5 ಗಂಟೆ."),
  },
  {
    slug: "vizag-araku", type: "ROUND", km: 950, hours: 60, when: ["DAYS", 3],
    name: L("విశాఖ + అరకు", "Vizag + Araku", "विशाखापत्तनम + अराकू", "விசாகப்பட்டினம் + அரக்கு", "ವಿಶಾಖಪಟ್ಟಣ + ಅರಕು"),
    to: "Araku Valley, Andhra Pradesh, India",
    stops: [{ v: "Visakhapatnam, Andhra Pradesh, India", n: L("విశాఖపట్నం", "Visakhapatnam", "विशाखापत्तनम", "விசாகப்பட்டினம்", "ವಿಶಾಖಪಟ್ಟಣ") }],
    end: L("అరకు", "Araku", "अराकू", "அரக்கு", "ಅರಕು"),
    note: L("విశాఖ బీచ్‌లు, అరకు లోయ, బొర్రా గుహలు - 3 రోజుల కుటుంబ యాత్ర.",
      "Vizag beaches, Araku valley and Borra caves - a 3-day family trip.",
      "विशाखापत्तनम के समुद्र तट, अराकू घाटी और बोर्रा गुफाएँ - 3 दिन की पारिवारिक यात्रा।",
      "விசாகப்பட்டினம் கடற்கரை, அரக்கு பள்ளத்தாக்கு, பொர்ரா குகைகள் - 3 நாள் குடும்பப் பயணம்.",
      "ವಿಶಾಖಪಟ್ಟಣ ಕಡಲತೀರ, ಅರಕು ಕಣಿವೆ, ಬೊರ್ರಾ ಗುಹೆಗಳು - 3 ದಿನಗಳ ಕುಟುಂಬ ಪ್ರವಾಸ."),
  },
  {
    slug: "bhadrachalam", type: "ROUND", km: 350, hours: 12, when: ["SAME"],
    name: L("భద్రాచలం", "Bhadrachalam", "भद्राचलम", "பத்ராசலம்", "ಭದ್ರಾಚಲಂ"),
    to: "Bhadrachalam, Telangana, India",
    note: L("సీతారాముల దర్శనం - ఉదయం వెళ్ళి అదే రోజు తిరిగి.",
      "Sri Sita Ramachandra Swamy darshan - go in the morning, back the same day.",
      "सीता-राम दर्शन - सुबह जाएँ, उसी दिन लौटें।",
      "சீதா ராமர் தரிசனம் - காலையில் சென்று அன்றே திரும்பலாம்.",
      "ಸೀತಾರಾಮ ದರ್ಶನ - ಬೆಳಿಗ್ಗೆ ಹೋಗಿ ಅದೇ ದಿನ ಹಿಂತಿರುಗಿ."),
  },
  {
    slug: "annavaram", type: "ROUND", km: 360, hours: 12, when: ["SAME"],
    name: L("అన్నవరం", "Annavaram", "अन्नवरम", "அன்னவரம்", "ಅನ್ನವರಂ"),
    to: "Annavaram, Andhra Pradesh, India",
    note: L("సత్యనారాయణ స్వామి వ్రతం - ఉదయం వెళ్ళి అదే రోజు తిరిగి.",
      "Satyanarayana Swamy vratam - go in the morning, back the same day.",
      "सत्यनारायण स्वामी व्रत - सुबह जाएँ, उसी दिन लौटें।",
      "சத்யநாராயண சுவாமி விரதம் - காலையில் சென்று அன்றே திரும்பலாம்.",
      "ಸತ್ಯನಾರಾಯಣ ಸ್ವಾಮಿ ವ್ರತ - ಬೆಳಿಗ್ಗೆ ಹೋಗಿ ಅದೇ ದಿನ ಹಿಂತಿರುಗಿ."),
  },
  {
    slug: "arunachalam", type: "ROUND", km: 1040, hours: 48, when: ["DAYS", 2], tag: "KARTHIKA",
    name: L("అరుణాచలం", "Arunachalam", "अरुणाचलम", "அருணாசலம்", "ಅರುಣಾಚಲ"),
    to: "Tiruvannamalai, Tamil Nadu, India",
    note: L("గిరి ప్రదక్షిణ, కార్తీక దీపం - 2 రోజుల యాత్ర.",
      "Giri pradakshina and the Karthika Deepam - a 2-day trip.",
      "गिरि प्रदक्षिणा और कार्तिक दीपम - 2 दिन की यात्रा।",
      "கிரிவலம், கார்த்திகை தீபம் - 2 நாள் பயணம்.",
      "ಗಿರಿ ಪ್ರದಕ್ಷಿಣೆ, ಕಾರ್ತಿಕ ದೀಪ - 2 ದಿನಗಳ ಯಾತ್ರೆ."),
  },
  {
    slug: "sankranti-hyderabad", type: "ROUND", km: 550, hours: 36, when: ["DAYS", 2], tag: "SANKRANTI",
    name: L("సంక్రాంతికి హైదరాబాద్", "Hyderabad for Sankranti", "संक्रांति पर हैदराबाद", "சங்கராந்திக்கு ஹைதராபாத்", "ಸಂಕ್ರಾಂತಿಗೆ ಹೈದರಾಬಾದ್"),
    dest: L("హైదరాబాద్", "Hyderabad", "हैदराबाद", "ஹைதராபாத்", "ಹೈದರಾಬಾದ್"), abbr: "Hyd",
    to: "Hyderabad, Telangana, India",
    note: L("పండక్కి కుటుంబాన్ని హైదరాబాద్ నుండి తీసుకురావడం, తిరిగి దింపడం.",
      "Bring the family home from Hyderabad for the festival, and back.",
      "त्योहार के लिए परिवार को हैदराबाद से लाना और वापस छोड़ना।",
      "பண்டிகைக்கு குடும்பத்தை ஹைதராபாத்திலிருந்து அழைத்து வந்து திரும்ப விடுதல்.",
      "ಹಬ್ಬಕ್ಕೆ ಕುಟುಂಬವನ್ನು ಹೈದರಾಬಾದ್‌ನಿಂದ ಕರೆತಂದು, ಹಿಂದೆ ಬಿಡುವುದು."),
  },
  {
    slug: "vijayawada-temples", type: "LOCAL", km: 80, hours: 8, when: ["HOURS", 8],
    name: L("విజయవాడ గుడులు", "Vijayawada temples", "विजयवाड़ा के मंदिर", "விஜயவாடா கோயில்கள்", "ವಿಜಯವಾಡ ದೇವಾಲಯಗಳು"),
    to: "Undavalli Caves, Andhra Pradesh, India",
    stops: [
      { v: "Kanaka Durga Temple, Indrakeeladri, Vijayawada, Andhra Pradesh, India", n: L("కనకదుర్గ గుడి", "Kanaka Durga temple", "कनक दुर्गा मंदिर", "கனக துர்கா கோயில்", "ಕನಕ ದುರ್ಗಾ ದೇವಾಲಯ") },
      { v: "Mangalagiri, Andhra Pradesh, India", n: L("మంగళగిరి", "Mangalagiri", "मंगलगिरि", "மங்களகிரி", "ಮಂಗಳಗಿರಿ") },
    ],
    end: L("ఉండవల్లి గుహలు", "Undavalli caves", "उंडवल्ली गुफाएँ", "உண்டவல்லி குகைகள்", "ಉಂಡವಲ್ಲಿ ಗುಹೆಗಳು"),
    note: L("కనకదుర్గ గుడి, మంగళగిరి, ఉండవల్లి గుహలు - 8 గంటల్లో.",
      "Kanaka Durga temple, Mangalagiri and Undavalli caves - in 8 hours.",
      "कनक दुर्गा मंदिर, मंगलगिरि और उंडवल्ली गुफाएँ - 8 घंटे में।",
      "கனக துர்கா கோயில், மங்களகிரி, உண்டவல்லி குகைகள் - 8 மணி நேரத்தில்.",
      "ಕನಕ ದುರ್ಗಾ ದೇವಾಲಯ, ಮಂಗಳಗಿರಿ, ಉಂಡವಲ್ಲಿ ಗುಹೆಗಳು - 8 ಗಂಟೆಗಳಲ್ಲಿ."),
  },
  dropTrip("visakhapatnam", 350, 7, "Visakhapatnam, Andhra Pradesh, India",
    L("విశాఖపట్నం", "Visakhapatnam", "विशाखापत्तनम", "விசாகப்பட்டினம்", "ವಿಶಾಖಪಟ್ಟಣ"), "Vizag"),
  dropTrip("chennai", 455, 9, "Chennai, Tamil Nadu, India", L("చెన్నై", "Chennai", "चेन्नई", "சென்னை", "ಚೆನ್ನೈ")),
  dropTrip("bengaluru", 640, 12, "Bengaluru, Karnataka, India", L("బెంగళూరు", "Bengaluru", "बेंगलुरु", "பெங்களூரு", "ಬೆಂಗಳೂರು"), "Blr"),
  dropTrip("rajahmundry", 155, 3, "Rajahmundry, Andhra Pradesh, India", L("రాజమండ్రి", "Rajahmundry", "राजमुंदरी", "ராஜமுந்திரி", "ರಾಜಮಂಡ್ರಿ")),
  dropTrip("kakinada", 210, 4, "Kakinada, Andhra Pradesh, India", L("కాకినాడ", "Kakinada", "काकीनाडा", "காக்கிநாடா", "ಕಾಕಿನಾಡ")),
  dropTrip("nellore", 275, 5, "Nellore, Andhra Pradesh, India", L("నెల్లూరు", "Nellore", "नेल्लोर", "நெல்லூர்", "ನೆಲ್ಲೂರು")),
  dropTrip("ongole", 150, 3, "Ongole, Andhra Pradesh, India", L("ఒంగోలు", "Ongole", "ओंगोल", "ஓங்கோல்", "ಒಂಗೋಲ್")),
  dropTrip("eluru", 60, 1, "Eluru, Andhra Pradesh, India", L("ఏలూరు", "Eluru", "एलुरु", "ஏலூரு", "ಏಲೂರು")),
  dropTrip("machilipatnam", 70, 2, "Machilipatnam, Andhra Pradesh, India", L("మచిలీపట్నం", "Machilipatnam", "मछलीपट्टनम", "மச்சிலிப்பட்டினம்", "ಮಚಲಿಪಟ್ಟಣ")),
  dropTrip("khammam", 125, 3, "Khammam, Telangana, India", L("ఖమ్మం", "Khammam", "खम्मम", "கம்மம்", "ಖಮ್ಮಂ")),
  dropTrip("amaravati", 35, 1, "Amaravati, Andhra Pradesh, India", L("అమరావతి", "Amaravati", "अमरावती", "அமராவதி", "ಅಮರಾವತಿ")),
];

export const SEASON_SLUGS = SEASON_TRIPS.map((t) => t.slug);
export const seasonTrip = (slug) => SEASON_TRIPS.find((t) => t.slug === slug) || null;

/* Back to Vijayawada (30 Oct 2026, mock Seo_BF change 2): "<town> to Vijayawada" - a
   Vijayawada car comes out, picks the customer up and drives them home, one way. Its page
   is /trips/<slug>-to-vijayawada. Not for the circuits, the season repeat, the local tour
   or Gannavaram airport (already the same fare both ways). km is one way. */
export const HOME_SUFFIX = "-to-vijayawada";
const NO_RETURN = new Set(["pancharama", "vizag-araku", "sabarimala", "sankranti-hyderabad", "vijayawada-temples", "gannavaram-airport"]);
export const RETURN_SLUGS = SEASON_TRIPS.filter((t) => !NO_RETURN.has(t.slug) && t.type !== "LOCAL").map((t) => t.slug);
export function returnTrip(slug) {
  const base = RETURN_SLUGS.includes(slug) ? seasonTrip(slug) : null;
  if (!base) return null;
  const km = base.type === "ROUND" ? Math.round(base.km / 10) * 5 : base.km;
  const p = base.dest || base.name;
  return {
    slug: `${slug}${HOME_SUFFIX}`, base: slug, back: true, type: "ONE_WAY", km,
    hours: base.type === "ONE_WAY" ? base.hours : Math.max(1, Math.round(km / 55)), when: ["DROP"],
    name: p, ...(base.abbr ? { abbr: base.abbr } : {}), from: base.to, to: SEASON_FROM.value,
    note: L(`విజయవాడ కారు ${p.te}కి వచ్చి, మిమ్మల్ని ఎక్కించుకుని విజయవాడకి తీసుకొస్తుంది.`,
      `A Vijayawada car comes to ${p.en}, picks you up and drives you to Vijayawada.`,
      `विजयवाड़ा की कार ${p.hi} आकर आपको विजयवाड़ा ले आएगी।`,
      `விஜயவாடா கார் ${p.ta} வந்து உங்களை விஜயவாடாவுக்கு அழைத்து வரும்.`,
      `ವಿಜಯವಾಡ ಕಾರು ${p.kn}ಗೆ ಬಂದು ನಿಮ್ಮನ್ನು ವಿಜಯವಾಡಕ್ಕೆ ಕರೆತರುತ್ತದೆ.`),
  };
}

/* Local hours in Vijayawada (mock Seo_BF change 3): the rows of the season pages for
   Bhavani Deeksha and weddings - 4, 8 and 12 hours, 10 km an hour. */
export const LOCAL_HOURS = [4, 8, 12].map((h) => ({
  slug: `local-${h}`, type: "LOCAL", km: h * 10, hours: h, when: ["HOURS", h], to: SEASON_FROM.value, local: true,
  name: L(`లోకల్ ${h} గంటలు / ${h * 10} కి.మీ.`, `Local ${h} hours / ${h * 10} km`, `लोकल ${h} घंटे / ${h * 10} कि.मी.`,
    `உள்ளூர் ${h} மணி நேரம் / ${h * 10} கி.மீ.`, `ಸ್ಥಳೀಯ ${h} ಗಂಟೆ / ${h * 10} ಕಿ.ಮೀ.`),
}));

/** Any trip a page can show: a listed one, one back to Vijayawada, or local hours. */
export function anyTrip(slug) {
  const s = String(slug || "");
  return seasonTrip(s) || (s.endsWith(HOME_SUFFIX) ? returnTrip(s.slice(0, -HOME_SUFFIX.length)) : null)
    || LOCAL_HOURS.find((t) => t.slug === s) || null;
}

/* The words, in five languages. Telugu as spoken in Vijayawada; no avoidable English in
   te/hi/ta/kn. */
export const TRIP_TEXT = {
  title: {
    KARTHIKA: L("ఈ కార్తీక మాసంలో", "This Karthika masam", "इस कार्तिक मास में", "இந்த கார்த்திகை மாதத்தில்", "ಈ ಕಾರ್ತಿಕ ಮಾಸದಲ್ಲಿ"),
    SANKRANTI: L("ఈ సంక్రాంతికి", "This Sankranti", "इस संक्रांति पर", "இந்த சங்கராந்திக்கு", "ಈ ಸಂಕ್ರಾಂತಿಗೆ"),
    SUMMER: L("ఈ వేసవిలో", "This summer", "इस गर्मी में", "இந்த கோடையில்", "ಈ ಬೇಸಿಗೆಯಲ್ಲಿ"),
    POPULAR: L("విజయవాడ నుండి ప్రముఖ యాత్రలు", "Popular trips from Vijayawada", "विजयवाड़ा से लोकप्रिय यात्राएँ", "விஜயவாடாவிலிருந்து பிரபல பயணங்கள்", "ವಿಜಯವಾಡದಿಂದ ಜನಪ್ರಿಯ ಪ್ರವಾಸಗಳು"),
  },
  tag: {
    KARTHIKA: L("కార్తీకం", "Karthika", "कार्तिक", "கார்த்திகை", "ಕಾರ್ತಿಕ"),
    AYYAPPA: L("అయ్యప్ప", "Ayyappa", "अय्यप्पा", "ஐயப்பன்", "ಅಯ್ಯಪ್ಪ"),
    SANKRANTI: L("సంక్రాంతి", "Sankranti", "संक्रांति", "சங்கராந்தி", "ಸಂಕ್ರಾಂತಿ"),
  },
  when: {
    DAY: L("ఒక రోజు", "1 day", "1 दिन", "1 நாள்", "1 ದಿನ"),
    DAYS: L("{n} రోజులు", "{n} days", "{n} दिन", "{n} நாட்கள்", "{n} ದಿನಗಳು"),
    SAME: L("ఒకే రోజు", "Same day", "उसी दिन", "அதே நாள்", "ಅದೇ ದಿನ"),
    DROP: L("ఒక వైపు", "One way", "एक तरफ़", "ஒரு வழி", "ಒಂದು ಕಡೆ"),
    HOURS: L("{n} గంటలు", "{n} hours", "{n} घंटे", "{n} மணி நேரம்", "{n} ಗಂಟೆ"),
  },
  group: {
    HATCH: L("హ్యాచ్‌బ్యాక్", "Hatchback", "हैचबैक", "ஹேட்ச்பேக்", "ಹ್ಯಾಚ್‌ಬ್ಯಾಕ್"),
    SEDAN: L("సెడాన్", "Sedan", "सेडान", "செடான்", "ಸೆಡಾನ್"),
    SUV: L("ఎస్‌యూవీ", "SUV", "एसयूवी", "எஸ்யூவி", "ಎಸ್‌ಯುವಿ"),
    SEVEN: L("7 సీటర్", "7-seater", "7 सीटर", "7 இருக்கை", "7 ಸೀಟರ್"),
    TEMPO: L("టెంపో / మినీ బస్", "Tempo / mini bus", "टेम्पो / मिनी बस", "டெம்போ / சிறு பேருந்து", "ಟೆಂಪೋ / ಮಿನಿ ಬಸ್"),
  },
  km: L("{km} కి.మీ", "{km} km", "{km} कि.मी.", "{km} கி.மீ", "{km} ಕಿ.ಮೀ"),
  groupTrip: L("బృందం", "Group", "समूह", "குழு", "ಗುಂಪು"),
  sub: L("విజయవాడ నుండి · డ్రైవర్ బత్తా కలిపి", "From Vijayawada · driver allowance included", "विजयवाड़ा से · ड्राइवर भत्ता शामिल",
    "விஜயவாடாவிலிருந்து · ஓட்டுநர் படி சேர்த்து", "ವಿಜಯವಾಡದಿಂದ · ಚಾಲಕರ ಭತ್ಯೆ ಸೇರಿ"),
  cardFrom: L("{type} {price} నుండి", "{type} from {price}", "{type} {price} से", "{type} {price} முதல்", "{type} {price} ರಿಂದ"),
  from: L("{price} నుండి", "from {price}", "{price} से", "{price} முதல்", "{price} ರಿಂದ"),
  heading: L("{name} - విజయవాడ నుండి కారు", "{name} - car from Vijayawada", "{name} - विजयवाड़ा से कार", "{name} - விஜயவாடாவிலிருந்து கார்", "{name} - ವಿಜಯವಾಡದಿಂದ ಕಾರು"),
  about: L("సుమారు {km} కి.మీ · {when}", "About {km} km · {when}", "लगभग {km} कि.मी. · {when}", "சுமார் {km} கி.மீ · {when}", "ಸುಮಾರು {km} ಕಿ.ಮೀ · {when}"),
  people: L("{n} మంది", "{n} people", "{n} लोग", "{n} பேர்", "{n} ಜನ"),
  cars: L("{n} కార్లు", "{n} cars", "{n} गाड़ियाँ", "{n} கார்கள்", "{n} ಕಾರುಗಳು"),
  car1: L("1 కారు", "1 car", "1 गाड़ी", "1 கார்", "1 ಕಾರು"),
  vehicles: L("{n} వాహనాలు", "{n} vehicles", "{n} वाहन", "{n} வாகனங்கள்", "{n} ವಾಹನಗಳು"),
  fine: L("డ్రైవర్ బత్తా కలిపి · టోల్, పార్కింగ్ అదనం · ప్రతి కారు ధర దాని డ్రైవర్ పెట్టినదే",
    "Driver allowance included · tolls and parking extra · each car's price is set by its driver",
    "ड्राइवर भत्ता शामिल · टोल, पार्किंग अलग · हर गाड़ी का दाम उसके ड्राइवर ने तय किया है",
    "ஓட்டுநர் படி சேர்த்து · சுங்கம், வாகன நிறுத்தம் தனி · ஒவ்வொரு காரின் விலையும் அதன் டிரைவர் வைத்தது",
    "ಚಾಲಕರ ಭತ್ಯೆ ಸೇರಿ · ಟೋಲ್, ನಿಲುಗಡೆ ಶುಲ್ಕ ಬೇರೆ · ಪ್ರತಿ ಕಾರಿನ ದರ ಅದರ ಡ್ರೈವರ್ ಇಟ್ಟದ್ದು"),
  working: L("ధరలు లెక్కిస్తున్నాం…", "Working out the prices…", "दाम निकाल रहे हैं…", "விலைகளைக் கணக்கிடுகிறோம்…", "ದರಗಳನ್ನು ಲೆಕ್ಕ ಹಾಕುತ್ತಿದ್ದೇವೆ…"),
  carsTitle: L("ఈ ట్రిప్‌కి కార్లు", "Cars for this trip", "इस यात्रा के लिए गाड़ियाँ", "இந்தப் பயணத்துக்கான கார்கள்", "ಈ ಪ್ರವಾಸಕ್ಕೆ ಕಾರುಗಳು"),
  callBook: L("బుక్ చేయడానికి కాల్ చేయండి · ఉదయం 8 – రాత్రి 9", "Call us to book · 8 am – 9 pm", "बुक करने के लिए कॉल करें · सुबह 8 – रात 9",
    "முன்பதிவுக்கு அழையுங்கள் · காலை 8 – இரவு 9", "ಬುಕ್ ಮಾಡಲು ಕರೆ ಮಾಡಿ · ಬೆಳಿಗ್ಗೆ 8 – ರಾತ್ರಿ 9"),
  wa: L("వాట్సాప్‌లో అడగండి", "Ask on WhatsApp", "व्हाट्सऐप पर पूछें", "வாட்ஸ்அப்பில் கேளுங்கள்", "ವಾಟ್ಸಾಪ್‌ನಲ್ಲಿ ಕೇಳಿ"),
  waText: L("నమస్తే, {name} యాత్రకు కారు కావాలి.", "Hi, I need a car for {name}.", "नमस्ते, {name} यात्रा के लिए गाड़ी चाहिए।",
    "வணக்கம், {name} பயணத்துக்கு கார் வேண்டும்.", "ನಮಸ್ಕಾರ, {name} ಪ್ರವಾಸಕ್ಕೆ ಕಾರು ಬೇಕು."),
  faq: L("తరచుగా అడిగే ప్రశ్నలు", "Questions people ask", "अक्सर पूछे जाने वाले सवाल", "அடிக்கடி கேட்கப்படும் கேள்விகள்", "ಆಗಾಗ ಕೇಳುವ ಪ್ರಶ್ನೆಗಳು"),
  q1: L("{name} యాత్రకు కారు ధర ఎంత?", "How much is a car for {name}?", "{name} यात्रा के लिए गाड़ी कितने की है?",
    "{name} பயணத்துக்கு கார் விலை எவ்வளவு?", "{name} ಪ್ರವಾಸಕ್ಕೆ ಕಾರಿನ ದರ ಎಷ್ಟು?"),
  a1: L("విజయవాడ నుండి {type} {price} నుండి - డ్రైవర్ బత్తా కలిపి. టోల్, పార్కింగ్ అదనం. ప్రతి కారు ధర దాని డ్రైవర్ పెట్టినదే.",
    "From Vijayawada, a {type} from {price} - driver allowance included. Tolls and parking are extra. Each car's price is set by its owner.",
    "विजयवाड़ा से {type} {price} से - ड्राइवर भत्ता शामिल। टोल, पार्किंग अलग। हर गाड़ी का दाम उसके ड्राइवर ने तय किया है।",
    "விஜயவாடாவிலிருந்து {type} {price} முதல் - ஓட்டுநர் படி சேர்த்து. சுங்கம், வாகன நிறுத்தம் தனி. ஒவ்வொரு காரின் விலையும் அதன் டிரைவர் வைத்தது.",
    "ವಿಜಯವಾಡದಿಂದ {type} {price} ರಿಂದ - ಚಾಲಕರ ಭತ್ಯೆ ಸೇರಿ. ಟೋಲ್, ನಿಲುಗಡೆ ಶುಲ್ಕ ಬೇರೆ. ಪ್ರತಿ ಕಾರಿನ ದರ ಅದರ ಡ್ರೈವರ್ ಇಟ್ಟದ್ದು."),
  a1Plain: L("ప్రతి కారు ధర దాని డ్రైవర్ పెట్టినదే - ఈ పేజీలో కారు రకం ప్రకారం తక్కువ ధర చూడండి. డ్రైవర్ బత్తా కలిపి; టోల్, పార్కింగ్ అదనం.",
    "Each car's price is set by its driver - this page shows the lowest price for each car type. Driver allowance included; tolls and parking extra.",
    "हर गाड़ी का दाम उसके ड्राइवर ने तय किया है - इस पेज पर हर तरह की गाड़ी का सबसे कम दाम देखें। ड्राइवर भत्ता शामिल; टोल, पार्किंग अलग।",
    "ஒவ்வொரு காரின் விலையும் அதன் டிரைவர் வைத்தது - இந்தப் பக்கத்தில் ஒவ்வொரு வகைக்கும் குறைந்த விலை உள்ளது. ஓட்டுநர் படி சேர்த்து; சுங்கம், வாகன நிறுத்தம் தனி.",
    "ಪ್ರತಿ ಕಾರಿನ ದರ ಅದರ ಡ್ರೈವರ್ ಇಟ್ಟದ್ದು - ಈ ಪುಟದಲ್ಲಿ ಪ್ರತಿ ಬಗೆಯ ಕಾರಿನ ಕಡಿಮೆ ದರ ನೋಡಿ. ಚಾಲಕರ ಭತ್ಯೆ ಸೇರಿ; ಟೋಲ್, ನಿಲುಗಡೆ ಶುಲ್ಕ ಬೇರೆ."),
  q2: L("ఎంత సమయం పడుతుంది?", "How long does it take?", "कितना समय लगता है?", "எவ்வளவு நேரம் ஆகும்?", "ಎಷ್ಟು ಸಮಯ ಬೇಕು?"),
  a2: L("సుమారు {km} కి.మీ, {when}. {note}", "About {km} km, {when}. {note}", "लगभग {km} कि.मी., {when}। {note}",
    "சுமார் {km} கி.மீ, {when}. {note}", "ಸುಮಾರು {km} ಕಿ.ಮೀ, {when}. {note}"),
  q3: L("ఎలా బుక్ చేయాలి?", "How do I book?", "बुक कैसे करें?", "எப்படி முன்பதிவு செய்வது?", "ಬುಕ್ ಮಾಡುವುದು ಹೇಗೆ?"),
  a3: L("కారు ఎంచుకుని డ్రైవర్‌కు నేరుగా కాల్ చేయండి - బుకింగ్ ఫీజు లేదు, అడ్వాన్స్ లేదు.",
    "Pick a car and call its driver directly - no booking fee, no advance.",
    "गाड़ी चुनें और ड्राइवर को सीधे कॉल करें - कोई बुकिंग फ़ीस नहीं, कोई एडवांस नहीं।",
    "காரைத் தேர்ந்தெடுத்து டிரைவரை நேரடியாக அழையுங்கள் - முன்பதிவுக் கட்டணம் இல்லை, முன்பணம் இல்லை.",
    "ಕಾರು ಆರಿಸಿ ಡ್ರೈವರ್‌ಗೆ ನೇರವಾಗಿ ಕರೆ ಮಾಡಿ - ಬುಕಿಂಗ್ ಶುಲ್ಕ ಇಲ್ಲ, ಮುಂಗಡ ಇಲ್ಲ."),
  notFound: L("ఈ యాత్ర దొరకలేదు", "This trip was not found", "यह यात्रा नहीं मिली", "இந்தப் பயணம் கிடைக்கவில்லை", "ಈ ಪ್ರವಾಸ ಸಿಗಲಿಲ್ಲ"),
  home: L("అన్ని కార్లు చూడండి", "See all cars", "सभी गाड़ियाँ देखें", "எல்லா கார்களையும் பாருங்கள்", "ಎಲ್ಲಾ ಕಾರುಗಳನ್ನು ನೋಡಿ"),
  // The Home banner and its sheet (mock Trips_8).
  origin: L("విజయవాడ", "Vijayawada", "विजयवाड़ा", "விஜயவாடா", "ವಿಜಯವಾಡ"),
  originShort: L("విజయవాడ", "Vij", "विजयवाड़ा", "விஜயவாடா", "ವಿಜಯವಾಡ"),
  more: L("+{n}", "+{n}", "+{n}", "+{n}", "+{n}"),
  seeAll: L("{n} యాత్రలు ›", "See all {n} ›", "{n} यात्राएँ ›", "{n} பயணங்கள் ›", "{n} ಪ್ರವಾಸಗಳು ›"),
  sheetSub: L("విజయవాడ నుండి · తక్కువ ధర · డ్రైవర్ బత్తా కలిపి", "From Vijayawada · the lowest price · driver allowance included",
    "विजयवाड़ा से · सबसे कम दाम · ड्राइवर भत्ता शामिल", "விஜயவாடாவிலிருந்து · குறைந்த விலை · ஓட்டுநர் படி சேர்த்து",
    "ವಿಜಯವಾಡದಿಂದ · ಕಡಿಮೆ ದರ · ಚಾಲಕರ ಭತ್ಯೆ ಸೇರಿ"),
  tapHint: L("యాత్ర మీద నొక్కితే - ప్రతి కారు రకం ధర, కార్లు, వాట్సాప్", "Tap a trip - prices for each car type, the cars, WhatsApp",
    "यात्रा पर दबाएँ - हर तरह की गाड़ी का दाम, गाड़ियाँ, व्हाट्सऐप", "பயணத்தைத் தொடுங்கள் - ஒவ்வொரு வகை விலை, கார்கள், வாட்ஸ்அப்",
    "ಪ್ರವಾಸ ಒತ್ತಿ - ಪ್ರತಿ ಬಗೆಯ ದರ, ಕಾರುಗಳು, ವಾಟ್ಸಾಪ್"),
  close: L("మూసివేయి", "Close", "बंद करें", "மூடு", "ಮುಚ್ಚಿ"),
  // The banner and its sheet (mock Fix_A change 3, Fix_B change 4).
  popular: L("ప్రముఖ యాత్రలు", "Popular trips", "लोकप्रिय यात्राएँ", "பிரபல பயணங்கள்", "ಜನಪ್ರಿಯ ಪ್ರವಾಸಗಳು"),
  explore: L("చూడండి ›", "Explore ›", "देखें ›", "பாருங்கள் ›", "ನೋಡಿ ›"),
  byPrice: L("తక్కువ ధర", "Lowest price", "सबसे कम दाम", "குறைந்த விலை", "ಕಡಿಮೆ ದರ"),
  driverWord: L("డ్రైవర్", "Driver", "ड्राइवर", "ஓட்டுநர்", "ಚಾಲಕ"),
  driverSub: L("మీ కారు, మా డ్రైవర్ · తక్కువ డ్రైవర్ ఛార్జీ", "Your car, our driver · the lowest driver fare", "आपकी गाड़ी, हमारा ड्राइवर · सबसे कम किराया",
    "உங்கள் கார், எங்கள் ஓட்டுநர் · குறைந்த கட்டணம்", "ನಿಮ್ಮ ಕಾರು, ನಮ್ಮ ಚಾಲಕ · ಕಡಿಮೆ ಶುಲ್ಕ"),
  byDistance: L("తక్కువ దూరం", "Shortest distance", "सबसे कम दूरी", "குறைந்த தூரம்", "ಕಡಿಮೆ ದೂರ"),
  // /trips - every trip on one page (mock Seo_1).
  allTitle: L("విజయవాడ నుండి కార్లు", "Cabs from Vijayawada", "विजयवाड़ा से गाड़ियाँ", "விஜயவாடாவிலிருந்து கார்கள்", "ವಿಜಯವಾಡದಿಂದ ಕಾರುಗಳು"),
  allSub: L("రౌండ్ ట్రిప్‌లు, దిగబెట్టడం, విమానాశ్రయం - ప్రతి దానికి తక్కువ ధర", "Round trips, drops and the airport - the lowest price on each",
    "आना-जाना, छोड़ना और हवाई अड्डा - हर एक का सबसे कम दाम", "சென்று திரும்புதல், இறக்கிவிடுதல், விமான நிலையம் - ஒவ்வொன்றுக்கும் குறைந்த விலை",
    "ಹೋಗಿ ಬರುವುದು, ಬಿಡುವುದು, ವಿಮಾನ ನಿಲ್ದಾಣ - ಪ್ರತಿಯೊಂದಕ್ಕೂ ಕಡಿಮೆ ದರ"),
  allLink: L("అన్ని యాత్రలు ›", "All trips ›", "सभी यात्राएँ ›", "எல்லா பயணங்களும் ›", "ಎಲ್ಲಾ ಪ್ರವಾಸಗಳು ›"),
  others: L("విజయవాడ నుండి ఇతర యాత్రలు", "Other trips from Vijayawada", "विजयवाड़ा से दूसरी यात्राएँ", "விஜயவாடாவிலிருந்து மற்ற பயணங்கள்", "ವಿಜಯವಾಡದಿಂದ ಇತರ ಪ್ರವಾಸಗಳು"),
  section: {
    ROUND: L("వెళ్ళి రావడం", "Round trips", "आना-जाना", "சென்று திரும்புதல்", "ಹೋಗಿ ಬರುವುದು"),
    ONE_WAY: L("దిగబెట్టడం · విమానాశ్రయం", "Drops · airport", "छोड़ना · हवाई अड्डा", "இறக்கிவிடுதல் · விமான நிலையம்", "ಬಿಡುವುದು · ವಿಮಾನ ನಿಲ್ದಾಣ"),
    LOCAL: L("విజయవాడలోనే", "In Vijayawada", "विजयवाड़ा में ही", "விஜயவாடாவிலேயே", "ವಿಜಯವಾಡದಲ್ಲೇ"),
  },
};

/** "Vijayawada → Hyderabad"; a local tour is just its name. */
export const tripRoute = (trip, lang) => (trip.type === "LOCAL"
  ? tripText(trip.name, lang)
  : `${tripText(TRIP_TEXT.origin, lang)} → ${tripText(trip.dest || trip.name, lang)}`);
/** The banner's short form: "Hyd" in English, the place's name otherwise. */
export const tripShort = (trip, lang) => (lang === "en" && trip.abbr ? trip.abbr : tripText(trip.dest || trip.name, lang));

/** A text in the reader's language, with {name}-style blanks filled. */
export function tripText(entry, lang, vars = {}) {
  const s = (entry && (entry[lang] || entry.en)) || "";
  return s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m));
}

/** "2 రోజులు", "ఒకే రోజు" - how long, in the reader's language. */
export const tripWhenText = (trip, lang) => tripText(TRIP_TEXT.when[trip.when[0]], lang, { n: trip.when[1] });

/* The five groups a trip page lists, by the car's price category. A 7-seat SUV seats
   six passengers, so it is a 7-seater here whatever its comfort level. */
export const TRIP_GROUPS = ["HATCH", "SEDAN", "SUV", "SEVEN", "TEMPO"];
export const GROUP_PEOPLE = { HATCH: 4, SEDAN: 4, SUV: 4, SEVEN: 6 };
export function tripGroup(car) {
  const k = String(car?.category || "").toUpperCase();
  if (k.startsWith("HATCHBACK")) return "HATCH";
  if (k.startsWith("SEDAN")) return "SEDAN";
  if (k.startsWith("MINI_SUV") || k === "SUV_5") return "SUV";
  if (k.startsWith("SUV_7")) return "SEVEN";
  if (k.startsWith("TEMPO") || k.startsWith("MINI_BUS") || k.startsWith("BUS")) return "TEMPO";
  // A category an admin added: by its seats.
  const seats = Number(car?.carDetails?.seats ?? car?.seatCount ?? car?.seats) || 0;
  if (seats >= 9) return "TEMPO";
  if (seats >= 6) return "SEVEN";
  return seats > 0 ? "SEDAN" : null;
}

/** The lowest price and how many cars for each group, for one trip. calculateFare is
    passed in - the one the car cards use - so the two can never disagree. */
export function tripPrices(trip, cars, cityPricing, calculateFare) {
  const out = {};
  for (const car of cars || []) {
    const g = tripGroup(car);
    if (!g) continue;
    let fare = null;
    try { fare = calculateFare(car, trip.hours, trip.km, trip.type, cityPricing, {}); } catch { fare = null; }
    const p = Math.round(Number(fare?.final) || 0);
    if (!(p > 0)) continue;
    /* A price under a third of the usual fare is a typing slip, not an offer - an owner's
       14-rupee drop "up to 75 km" was a per-km rate in the wrong box (Oct 2026). It is not
       anybody's "from ₹" for the trip; the car still shows its own price on its card. */
    if (Number(fare?.original) > 0 && p < Number(fare.original) / 3) continue;
    const row = out[g] || (out[g] = { group: g, price: Infinity, count: 0 });
    row.count++;
    if (p < row.price) row.price = p;
  }
  // The cheapest first; the same price keeps the smaller car first.
  return TRIP_GROUPS.filter((g) => out[g]).map((g) => out[g]).sort((a, b) => a.price - b.price);
}

/** What Home shows for a trip: its lowest price, whatever the type - mostly a hatchback. */
export function cardPrice(trip, rows) {
  if (!rows?.length) return null;
  return rows.reduce((a, b) => (b.price < a.price ? b : a));
}

export const rupees = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

/** A trip page's title in the words people search (mock Seo_1):
    "Vijayawada to Tirupati Cab from ₹… - Round Trip | Sramika". */
export function seoTitle(trip, price) {
  const from = price ? ` from ${rupees(price)}` : "";
  if (trip.type === "LOCAL") return `${trip.name.en} Tour by Car${from} | Sramika`;
  const place = trip.tag === "SANKRANTI" ? trip.name.en : (trip.dest || trip.name).en;
  return `Vijayawada to ${place} Cab${from} - ${trip.type === "ONE_WAY" ? "One Way" : "Round Trip"} | Sramika`;
}
